import styled, { css } from 'styled-components';
import PlayLinkContainer from '../../components/Link/PlayLinkContainer';
import { device } from '../breakpoints';
import {
    extraLightCoolGrey,
    fontWeightBold,
    regularBlack,
    regularWhite,
    playGrey15,
    playGrey20,
} from '../design-tokens';
import { playFontSize } from '../typography/helpers';
import { Uppercase } from '../typography/PlayUppercase.styles';
import { BUTTON_SIZE, BUTTON_THEME } from './PlayButton';

const backgroundColorLightTheme = 'rgba(255, 255, 255, 0.8)';

const getColors = (color, bgColor, hoverColor, isDisabled) => {
    return css`
        color: ${color};
        background-color: ${bgColor};

        ${!isDisabled &&
        css`
            &:hover {
                background-color: ${hoverColor};
            }
        `}

        &:focus {
            border-color: ${color};
        }

        &:focus:not(:focus-visible) {
            border-color: transparent;
        }
    `;
};

const getTheme = (theme = BUTTON_THEME.DEFAULT, isDisabled = false) => {
    if (theme === BUTTON_THEME.LIGHT) {
        return getColors(regularBlack, backgroundColorLightTheme, regularWhite, isDisabled);
    }

    if (theme === BUTTON_THEME.WHITE) {
        return getColors(regularBlack, regularWhite, extraLightCoolGrey, isDisabled);
    }

    if (theme === BUTTON_THEME.DARK) {
        return getColors(regularWhite, playGrey15, playGrey20, isDisabled);
    }

    return getColors(regularWhite, 'rgba(255, 255, 255, 0.1)', 'rgba(255, 255, 255, 0.15)', isDisabled);
};

const getSize = (size) => {
    switch (size) {
        case BUTTON_SIZE.SMALL:
            return css`
                ${playFontSize(1.2)}
                border-radius: 100px;
                padding: 4px 20px;
            `;
        default:
            return css`
                ${playFontSize(1.3)}
                border-radius: 100px;
                padding: 9px 20px;
            `;
    }
};

export const ButtonStyles = css`
    ${(props) => getTheme(BUTTON_THEME.DEFAULT, props.isDisabled)};
    ${(props) => getSize(props.size)}
    font-weight: ${fontWeightBold};
    ${Uppercase}
    display: block;
    box-sizing: border-box;
    white-space: nowrap;
    cursor: pointer;
    outline: 0;
    text-decoration: none;
    border: 2px solid transparent;

    &:focus,
    &:hover,
    &:active {
        text-decoration: none;
        outline: 0;
    }
`;

export const ButtonLink = styled(PlayLinkContainer)`
    ${ButtonStyles}
    ${(props) => getTheme(props.theme, props.isDisabled)}
    ${(props) => getSize(props.size)}
    
    text-align: center;

    ${(props) =>
        props.isDisabled &&
        css`
            opacity: 0.4;
        `}
    ${(props) =>
        props.isCentered &&
        css`
            @media ${device.GteSmall} {
                display: inline-block;
            }
        `}
`;

export const StyledAnchor = styled.a`
    ${ButtonStyles}
    ${(props) => getTheme(props.theme, props.isDisabled)}
    ${(props) => getSize(props.size)}

    text-align: center;

    ${(props) => props.isDisabled && 'opacity: 0.4;'}
    ${(props) =>
        props.isCentered &&
        css`
            @media ${device.GteSmall} {
                display: inline-block;
            }
        `}
`;

export const StyledButton = styled.button`
    ${ButtonStyles}
    ${(props) => getTheme(props.theme, props.isDisabled)}
    ${(props) => getSize(props.size)}
    text-align: center;

    ${(props) => props.isDisabled && 'opacity: 0.4;'}
    ${(props) =>
        props.isCentered &&
        css`
            @media ${device.GteSmall} {
                display: inline-block;
            }
        `}
`;

// Centers Button
export const ButtonContainer = styled.div`
    display: flex;
    @media ${device.GteSmall} {
        justify-content: center;
    }

    button {
        flex-grow: 1;
        @media ${device.GteSmall} {
            flex-grow: 0;
        }
    }
`;

export const ButtonLabel = styled.span`
    vertical-align: middle;
    line-height: 1.4;
`;
